import React, { useEffect, useState } from 'react';
import { Modal, Button, Spin, message } from 'antd';
import { CloseOutlined, DownloadOutlined, LoadingOutlined, ZoomInOutlined, ZoomOutOutlined, RotateRightOutlined } from '@ant-design/icons';

interface ImageLightboxProps {
  src: string;
  onClose: () => void;
  filename?: string;
}

const ImageLightbox: React.FC<ImageLightboxProps> = ({ src, onClose, filename }) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [scale, setScale] = useState(1);
  const [rotation, setRotation] = useState(0);
  
  // Close on Escape key press
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  // Handle download
  const handleDownload = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isDownloading) return;
    
    setIsDownloading(true);
    
    try {
      // Extract filename from URL or use provided filename or default
      const defaultFilename = filename || (() => {
        const urlParts = src.split('/');
        return urlParts[urlParts.length - 1] || 'image.jpg';
      })();
      
      // Fetch the file as blob
      const response = await fetch(src);
      if (!response.ok) throw new Error('Error loading file');
      
      const blob = await response.blob();
      
      // Create object URL for the blob
      const blobUrl = window.URL.createObjectURL(blob);
      
      // Create an invisible link and trigger download
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = defaultFilename;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      
      // Clean up
      setTimeout(() => {
        document.body.removeChild(a);
        window.URL.revokeObjectURL(blobUrl);
      }, 100);
    } catch (error) {
      console.error('Error downloading:', error);
      message.error('Failed to download file');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleZoomIn = (e: React.MouseEvent) => {
    e.stopPropagation();
    setScale(prev => Math.min(prev + 0.25, 3));
  };

  const handleZoomOut = (e: React.MouseEvent) => {
    e.stopPropagation();
    setScale(prev => Math.max(prev - 0.25, 0.5));
  };

  const handleRotate = (e: React.MouseEvent) => {
    e.stopPropagation();
    setRotation(prev => (prev + 90) % 360);
  };

  const handleImageLoad = () => {
    setLoading(false);
  };

  return (
    <Modal
      open={true}
      footer={null}
      closable={false}
      onCancel={onClose}
      width="100%"
      style={{ maxWidth: '100vw', margin: 0, padding: 0, top: 0 }}
      wrapClassName="fullscreen-modal"
      maskStyle={{ backgroundColor: 'rgba(0, 0, 0, 0.85)' }}
      bodyStyle={{ 
        height: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        padding: 0,
        position: 'relative' 
      }}
    >
      {loading && (
        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
          <Spin indicator={<LoadingOutlined style={{ fontSize: 32 }} spin />} />
        </div>
      )}
      
      <img
        src={src}
        alt="Full size"
        style={{ 
          maxWidth: '90%',
          maxHeight: '90%',
          objectFit: 'contain',
          transform: `scale(${scale}) rotate(${rotation}deg)`,
          transition: 'transform 0.3s ease',
          display: loading ? 'none' : 'block'
        }}
        onClick={(e) => e.stopPropagation()}
        onLoad={handleImageLoad}
      />
      
      <div 
        style={{ 
          position: 'absolute',
          top: 16,
          right: 16,
          display: 'flex',
          gap: 8
        }}
      >
        <Button
          type="primary"
          style={{
            color: 'white'
          }}
          shape="circle"
          icon={<CloseOutlined />}
          onClick={onClose}
          ghost
        />
      </div>
      
      <div 
        style={{ 
          position: 'absolute',
          bottom: 16,
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          gap: 8,
          background: 'rgba(0, 0, 0, 0.5)',
          padding: '8px 16px',
          borderRadius: 24
        }}
      >
        <Button
          type="primary"
          style={{
            color: 'white'
          }}
          shape="circle"
          icon={<ZoomOutOutlined />}
          onClick={handleZoomOut}
          ghost
          disabled={scale <= 0.5}
        />
        <Button
          type="primary"
          style={{
            color: 'white'
          }}
          shape="circle"
          icon={<ZoomInOutlined />}
          onClick={handleZoomIn}
          ghost
          disabled={scale >= 3}
        />
        <Button
          type="primary"
          style={{
            color: 'white'
          }}
          shape="circle"
          icon={<RotateRightOutlined />}
          onClick={handleRotate}
          ghost
        />
        <Button
          type="primary"
          style={{
            color: 'white'
          }}
          shape="circle"
          icon={isDownloading ? <LoadingOutlined /> : <DownloadOutlined />}
          onClick={handleDownload}
          loading={isDownloading}
          ghost
        />
      </div>
    </Modal>
  );
};

export default ImageLightbox; 